import { fetchContentfulContentModelData } from '@/common/utils/fetchContentfulContentModelData';
import { Filter, FilterValue } from '@/generated/contentfulTypes';

export interface FetchFilterValuesBasedOnCategoryIdReturnValue {
  category: Filter;
  filterValues: FilterValue[];
}

export default async function fetchFilterValuesBasedOnCategoryId(
  filterValues: FilterValue[]
): Promise<FetchFilterValuesBasedOnCategoryIdReturnValue[]> {
  if (!filterValues.length) return [];

  // Extract all unique category IDs from the filter values
  const categoryIds = [
    ...new Set(
      filterValues
        .filter((filterValue) => filterValue?.filterCategory)
        .map((filterValue) => filterValue.filterCategory?.sys.id)
        .filter(Boolean) as string[]
    ),
  ];

  if (!categoryIds.length) return [];

  // Fetch the categories data using the IDs
  const categories = await fetchContentfulContentModelData<Filter[]>({
    contentType: 'filter',
    field: 'sys { id } filterIdentifier filterTitle { tkey value }',
    where: { sys: { id_in: categoryIds } },
    limit: 50,
  });

  // Group filter values by category
  const categoriesMap = new Map<string, FilterValue[]>();

  filterValues.forEach((filterValue) => {
    if (filterValue?.filterCategory?.sys?.id) {
      const categoryId = filterValue.filterCategory.sys.id;
      if (!categoriesMap.has(categoryId)) {
        categoriesMap.set(categoryId, []);
      }
      categoriesMap.get(categoryId)?.push(filterValue);
    }
  });

  const categoriesWithFilterValues = categories.map((category) => ({
    category,
    filterValues: categoriesMap.get(category.sys.id) || [],
  }));

  // Deduplicate filter values in each category based on their ID
  const deduplicated = categoriesWithFilterValues.map(
    ({ category, filterValues }) => {
      const uniqueFilterValues = Array.from(
        new Map(filterValues.map((value) => [value.sys.id, value])).values()
      );
      return { category, filterValues: uniqueFilterValues };
    }
  );

  return deduplicated;
}
